import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { API } from '@/lib/api';
import { setMobileAccessToken } from '@/lib/mobileAuth';
import { isNativeRuntime } from '@/lib/runtime';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { Mail, Lock, ArrowLeft, LifeBuoy, CreditCard } from 'lucide-react';

const BrandIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M5 4h14v4.2L8.7 20H5.2l9.1-10.4H5V4z" />
  </svg>
);

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setUserData } = useAuth();
  const [loading, setLoading] = useState(false);
  const [requestingAccess, setRequestingAccess] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [recoveryEmail, setRecoveryEmail] = useState('');

  const preferredSegment = (() => {
    try {
      return window.localStorage.getItem('zapt_account_segment') === 'business' ? 'business' : 'personal';
    } catch {
      return 'personal';
    }
  })();
  const segmentLabel = preferredSegment === 'business' ? 'Business' : 'Pessoal';
  const nextPath = searchParams.get('next') || '';

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: loginEmail, password: loginPassword })
      });

      if (response.ok) {
        const userData = await response.json();
        if (isNativeRuntime() && userData?.access_token) {
          setMobileAccessToken(userData.access_token);
        }
        setUserData(userData);
        toast.success('Login realizado com sucesso!');

        if (userData.role === 'admin') {
          navigate('/admin', { replace: true });
        } else {
          navigate(nextPath || '/app', { replace: true });
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao fazer login');
      }
    } catch {
      toast.error('Erro ao conectar com o servidor');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestAccess = async () => {
    if (!recoveryEmail.trim()) {
      toast.error('Informe seu e-mail para solicitar reativação');
      return;
    }

    setRequestingAccess(true);
    try {
      const response = await fetch(`${API}/auth/request-access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: recoveryEmail.trim(),
          account_segment: preferredSegment,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'Não foi possível registrar a solicitação.');
      }

      toast.success(data.message || 'Solicitação recebida.');
    } catch (error) {
      toast.error(error.message || 'Erro ao solicitar reativação');
    } finally {
      setRequestingAccess(false);
    }
  };

  return (
    <div className="mobile-safe-shell bg-zinc-50 flex flex-col">
      <header className="pb-4">
        <Button
          variant="ghost"
          onClick={() => navigate(preferredSegment === 'business' ? '/business' : '/pessoal')}
          className="text-zinc-600"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar
        </Button>
      </header>

      <main className="mobile-safe-content flex-1 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-sky-500 to-violet-600 flex items-center justify-center">
                <BrandIcon className="w-5 h-5 text-white" />
              </div>
              <div className="text-left min-w-0">
                <span className="font-heading text-lg sm:text-xl font-semibold text-zinc-900 block leading-tight">Zapt Resumo</span>
                <span className="text-[10px] font-semibold tracking-[0.18em] text-sky-700 block leading-tight">{segmentLabel.toUpperCase()}</span>
              </div>
            </div>
            <CardTitle className="text-2xl">Acessar conta</CardTitle>
            <CardDescription>Entre apenas se você já tiver uma conta ativa no Zapt Resumo {segmentLabel}.</CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            <form onSubmit={handleEmailLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="login-email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                  <Input
                    id="login-email"
                    type="email"
                    placeholder="seu@email.com"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    className="pl-10"
                    required
                    data-testid="login-email"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="login-password">Senha</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                  <Input
                    id="login-password"
                    type="password"
                    placeholder="••••••••"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    className="pl-10"
                    required
                    data-testid="login-password"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full rounded-full"
                disabled={loading}
                data-testid="login-submit"
              >
                {loading ? 'Entrando...' : 'Entrar'}
              </Button>

              <Button
                type="button"
                variant="link"
                className="w-full text-sky-700"
                onClick={() => navigate(`/forgot-password?segment=${preferredSegment}${loginEmail ? `&email=${encodeURIComponent(loginEmail)}` : ''}`)}
              >
                Esqueci minha senha
              </Button>
            </form>

            <div className="relative">
              <Separator />
              <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-2 text-xs text-zinc-500">
                outras opções
              </span>
            </div>

            <div className="grid gap-3">
              <Button
                type="button"
                variant="outline"
                className="w-full rounded-full"
                onClick={() => navigate(preferredSegment === 'business' ? '/business#planos' : '/pessoal#planos')}
              >
                <CreditCard className="w-4 h-4 mr-2" />
                Escolher um plano
              </Button>

            </div>

            <div className="rounded-2xl border border-zinc-200 bg-zinc-50 p-4 space-y-3">
              <div className="flex items-center gap-2">
                <LifeBuoy className="w-4 h-4 text-sky-600" />
                <p className="text-sm font-medium text-zinc-900">Já teve acesso?</p>
              </div>
              <p className="text-xs text-zinc-500">
                Se você já teve cadastro e precisa voltar a usar o sistema, solicite a reativação do acesso pelo seu e-mail.
              </p>
              <div className="space-y-2">
                <Label htmlFor="recovery-email">E-mail cadastrado</Label>
                <Input
                  id="recovery-email"
                  type="email"
                  placeholder="seu@email.com"
                  value={recoveryEmail}
                  onChange={(e) => setRecoveryEmail(e.target.value)}
                />
              </div>
              <Button
                type="button"
                variant="outline"
                className="w-full rounded-full"
                disabled={requestingAccess}
                onClick={handleRequestAccess}
              >
                {requestingAccess ? 'Enviando...' : 'Solicitar reativação'}
              </Button>
            </div>

            <div className="rounded-2xl border border-violet-100 bg-violet-50 p-4 text-center">
              <div className="flex items-center justify-center gap-2 text-violet-700 mb-2">
                <span className="text-sm font-semibold">Novo no Zapt Resumo?</span>
              </div>
              <p className="text-xs text-violet-700/80">
                O acesso novo acontece pelo checkout do plano escolhido. Depois do pagamento confirmado, a conta é criada automaticamente.
              </p>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
